// The director plays the game as beats so the player can follow each
// change. A beat is a caption, some marks on the board, and sparks that
// travel between squares, held for a moment. The cue bar and the board read
// the current beat. The AI driver and the narrator write it. The game state
// never waits for a beat, but the AI does: it acts only when the stage is
// clear, no beat is playing, nothing is queued, and the board shows the live
// game. The queue also waits while the player looks at an earlier step, and
// while the board is covered for the setup of a match.

import { BEAT_PACE } from '../constants';
import { createStore } from '../lib/store';
import type { SpellPolarity } from '../spells/types';
import { boardCoverStore, isBoardOpen } from './boardCover';
import { historyStore, isReviewing } from './history';
import { isMatchWaiting, matchWaitStore } from './matchWait';
import { stageStore } from './stage';

/** `remove` is a piece that the pick takes from the board. It gets a cross. */
export type BeatMarkKind = 'consider' | 'pick' | 'lift' | 'land' | 'changed' | 'remove';

export type BeatMark = {
  square: number;
  kind: BeatMarkKind;
};

export type Spark = {
  from: number;
  to: number;
};

/** What colors a beat's marks: a spell polarity, a capture, or the rules. */
export type BeatTone = SpellPolarity | 'capture' | 'system';

export type Beat = {
  caption: string;
  detail: string;
  marks: readonly BeatMark[];
  sparks: readonly Spark[];
  /** Colors the marks. Null is Violet's own color, for its decisions. */
  tone: BeatTone | null;
  /** Dims every piece outside the marks. */
  spotlight: boolean;
};

export const beatStore = createStore<Beat | null>(null);

/** How many beats wait their turn. */
export const queueStore = createStore<number>(0);

let serial = 0;
const queue: { beat: Beat; ms: number; onStart: (() => void) | null }[] = [];
let draining = false;

/** A queued beat may start: the stage is clear, no beat plays, and the board is live and open. */
const canStartBeat = (): boolean =>
  stageStore.get().phase === 'hidden' &&
  beatStore.get() === null &&
  !isReviewing() &&
  isBoardOpen();

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const paced = (ms: number): number => Math.round(ms * BEAT_PACE);

/** Shows a beat for `ms` (scaled by the pace), then clears it unless a newer beat replaced it. */
export const playBeat = async (beat: Beat, ms: number): Promise<void> => {
  serial += 1;
  const mine = serial;
  beatStore.set(beat);
  await wait(paced(ms));
  if (serial === mine) beatStore.set(null);
};

/**
 * Shows a beat for `ms` (scaled by the pace) and leaves it in place. The
 * caller follows it with the next beat, so no queued beat starts between them.
 */
export const playBeatAndHold = async (beat: Beat, ms: number): Promise<void> => {
  holdBeat(beat);
  await wait(paced(ms));
};

/** Holds a beat with no end. The next beat, or clearBeat, replaces it. */
export const holdBeat = (beat: Beat): void => {
  serial += 1;
  beatStore.set(beat);
};

export const clearBeat = (): void => {
  serial += 1;
  beatStore.set(null);
};

/**
 * Lines a beat up behind the stage and any beat already playing. `onStart`
 * runs at the moment the beat shows.
 */
export const queueBeat = (beat: Beat, ms: number, onStart: (() => void) | null = null): void => {
  queue.push({ beat, ms, onStart });
  queueStore.set(queue.length);
  void drain();
};

export const dropQueue = (): void => {
  queue.length = 0;
  queueStore.set(0);
};

const drain = async (): Promise<void> => {
  if (draining) return;
  draining = true;
  try {
    while (queue.length > 0) {
      if (!canStartBeat()) return;
      const next = queue.shift();
      queueStore.set(queue.length);
      if (!next) continue;
      next.onStart?.();
      await playBeat(next.beat, next.ms);
    }
  } finally {
    draining = false;
    if (queue.length > 0 && canStartBeat()) void drain();
  }
};

stageStore.subscribe(() => {
  void drain();
});
beatStore.subscribe(() => {
  void drain();
});
historyStore.subscribe(() => {
  void drain();
});
boardCoverStore.subscribe(() => {
  void drain();
});

/** A match that waits for its casters is never quiet, so nobody rolls or moves in it. */
export const isQuiet = (): boolean => canStartBeat() && queue.length === 0 && !isMatchWaiting();

/** Resolves once nothing is being shown. The AI waits on this before it acts. */
export const whenQuiet = (): Promise<void> =>
  new Promise((resolve) => {
    const unsubscribers: (() => void)[] = [];
    const check = (): void => {
      if (!isQuiet()) return;
      for (const unsubscribe of unsubscribers) unsubscribe();
      resolve();
    };
    unsubscribers.push(
      stageStore.subscribe(check),
      beatStore.subscribe(check),
      queueStore.subscribe(check),
      historyStore.subscribe(check),
      matchWaitStore.subscribe(check),
      boardCoverStore.subscribe(check),
    );
    check();
  });

export const beat = (caption: string, detail = '', extra: Partial<Beat> = {}): Beat => ({
  caption,
  detail,
  marks: [],
  sparks: [],
  tone: null,
  spotlight: false,
  ...extra,
});
